import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import config from 'constants/apiConfig';

export type AorApprovalAssignee =
  | { kind: 'user'; userGuid: string; displayName: string }
  | { kind: 'role'; roleId: string; displayName: string };

export type AorReportApprovalPolicy = {
  steps: { assignees: AorApprovalAssignee[] }[];
  users: { userGuid: string; displayName: string }[];
  roles: { roleId: string; name: string }[];
  resolvedSteps: { title: string; people: string[] }[];
};

const policyKey = (aorId: string) => ['aorReportApprovalPolicy', aorId];

const policyUrl = (aorId: string) =>
  `${config.apiGateway.sitrepsV2}/aor/${aorId}/approval-policy`;

export const useAorReportApprovalPolicy = (aorId: string | null | undefined) =>
  useQuery({
    queryKey: policyKey(aorId ?? ''),
    queryFn: async () => {
      const response = await axios.get<AorReportApprovalPolicy>(
        policyUrl(aorId ?? '')
      );
      return response.data;
    },
    enabled: !!aorId,
  });

export const usePublishAorReport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sitrepId: string) => {
      await axios.post(`${config.apiGateway.sitrepsV2}/${sitrepId}/publish`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sitrepsV2'] });
    },
  });
};

export const useSaveAorReportApprovalPolicy = (aorId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      steps: {
        assignees: {
          kind: 'user' | 'role';
          userGuid?: string;
          roleId?: string;
        }[];
      }[]
    ) => {
      const response = await axios.put<AorReportApprovalPolicy>(
        policyUrl(aorId),
        { steps }
      );
      return response.data;
    },
    onSuccess: (policy) => {
      queryClient.setQueryData(policyKey(aorId), policy);
    },
  });
};
